import { Record } from "immutable";
import { t } from "utilities/localization/t";
import { BulkGroupUsersSummary } from "../../interfaces/groups/bulk-group-users-summary";

// -------------------------------------------------------------------------------------------------
// #region Default Values
// -------------------------------------------------------------------------------------------------

const defaultValues: BulkGroupUsersSummary = {
    existing: [],
    failed: [],
    successful: [],
    csvUploadResults: "",
};

// #endregion Default Values

// -------------------------------------------------------------------------------------------------
// #region Record
// -------------------------------------------------------------------------------------------------

class BulkGroupUsersSummaryRecord extends Record(defaultValues) implements BulkGroupUsersSummary {
    // -------------------------------------------------------------------------------------------------
    // #region Constructor
    // -------------------------------------------------------------------------------------------------

    constructor(params?: Partial<BulkGroupUsersSummary>) {
        params = params ?? Object.assign({}, defaultValues);

        super(params);
    }

    // #endregion Constructor

    // -------------------------------------------------------------------------------------------------
    // #region Public Methods
    // -------------------------------------------------------------------------------------------------

    /**
     * Convenience method to get the total bulk group records calculated from the resultObject successful, existing and failed properties length.
     *
     * @returns {number} total bulk group records calculated from the resultObject successful, existing and failed properties length.
     */
    public calculateTotalBulkGroupUsersRecords(): number {
        return (
            (this.existing.length || 0) + (this.successful.length || 0) + (this.failed.length || 0)
        );
    }

    /**
     * Convenience method to a summary details object for the bulk contract group summary.
     *
     * @returns {Array<{ name: string; value: any }>} An array of name/value pairs for the summary details.
     */
    public getSummaryDetails(): { name: string; value: any; description?: string }[] {
        return [
            {
                name: t("totalImported"),
                value: this.calculateTotalBulkGroupUsersRecords(),
            },
            {
                name: t("successfullyAddedBulkGroupUser"),
                value: this.successful.length,
                description: t("groupUsersInThisGroupWereSuccessfullyAdded"),
            },
            {
                name: t("alreadyAddedBulkGroupUser"),
                value: this.existing.length,
                description: t("groupUsersInThisGroupWereAlreadyAdded"),
            },
            {
                name: t("failedToAddBulkGroupUser"),
                value: this.failed.length,
                description: t("groupUsersInThisGroupFailedToAdd"),
            },
        ];
    }

    /**
     * Given a set of values for BulkGroupSummary properties, create a new
     * BulkGroupSummaryRecord object from this instance, overwriting the properties in the
     * values parameter with values provided.
     *
     * @param {Partial<BulkGroupUsersSummary>} values The values to overwrite on this instance.
     * @returns A BulkGroupSummaryRecord with values from this instance and the values parameter.
     */
    public with(values: Partial<BulkGroupUsersSummary>): BulkGroupUsersSummaryRecord {
        return new BulkGroupUsersSummaryRecord(Object.assign(this.toJS(), values));
    }

    // #endregion Public Methods
}

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { BulkGroupUsersSummaryRecord };

// #endregion Exports
